import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FaHistory, FaCheck, FaTimes } from 'react-icons/fa';
import { Api } from '../../../../services/api';

const PerformanceHistoryModal = ({ visible, onHide, userData, performanceItems }) => {
    const toast = useRef(null);
    const [historyData, setHistoryData] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const isAdmin = localStorage.getItem('isAdmin') === 'Y'; // Checking admin status

    // Dummy Data constant for testing/demo

    const generateDummyData = () => {
        const dummyHistory = [];
        const today = new Date();
        
        // Determine current week's Monday to align weeks correctly (Monday to Sunday)
        const dayOfWeek = today.getDay(); // 0 (Sun) to 6 (Sat)
        const currentMonday = new Date(today);
        // If today is Sunday (0), Monday was 6 days ago. If Monday (1), it is today (0 days ago).
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        currentMonday.setDate(today.getDate() - daysToSubtract);
        
        // Generate 10 weeks of data
        for (let i = 0; i < 10; i++) {
            const startDate = new Date(currentMonday);
            startDate.setDate(currentMonday.getDate() - (i * 7));
            
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6);

            // Generate performance items with random Yes/No
            const performance = performanceItems.map(item => ({
                ...item,
                value: Math.random() > 0.3 ? 'yes' : 'no' // 70% chance of Yes
            }));
            
            // Calculate score
            const yesCount = performance.filter(p => p.value === 'yes').length;
            // logic for priority failure (items 0 and 1)
            const isPriorityFail = performance[0].value === 'no' || performance[1].value === 'no';
            const score = isPriorityFail ? 0 : (yesCount / 10) * 100;

            dummyHistory.push({
                id: `dummy-${i}`,
                from_date: startDate.toISOString().split('T')[0],
                to_date: endDate.toISOString().split('T')[0],
                score: score,
                performance: performance
            });
        }
        return dummyHistory;
    };

    const handleItemChange = (index, field, value) => {
        if (!selectedHistoryItem) return;

        // Clone the selected item
        const updatedItem = { ...selectedHistoryItem };
        const updatedPerformance = [...updatedItem.performance];
        
        // Update the specific field
        updatedPerformance[index] = { ...updatedPerformance[index], [field]: value };
        updatedItem.performance = updatedPerformance;

        // Recalculate score only if value changes
        if (field === 'value') {
            const yesCount = updatedPerformance.filter(p => p.value === 'yes').length;
            const isPriorityFail = updatedPerformance[0].value === 'no' || updatedPerformance[1].value === 'no';
            const newScore = isPriorityFail ? 0 : (yesCount / 10) * 100;
            updatedItem.score = newScore;
        }

        // Update local state for immediate feedback in UI
        setSelectedHistoryItem(updatedItem);
        
        // Also update the list so the sidebar reflects changes immediately
        const updatedHistoryData = historyData.map(item => 
            item.id === updatedItem.id ? updatedItem : item
        );
        setHistoryData(updatedHistoryData);
        
        // Also update filtered list
        setFilteredHistory(updatedHistoryData.filter(item => {
            const itemYear = new Date(item.from_date).getFullYear();
            return itemYear === selectedYear;
        }));
    };

    const handleSave = async () => {
        if (!selectedHistoryItem) return;
        setIsSaving(true);
        
        try {
            // Check if this is a real record (has numeric id usually) or dummy (id starts with dummy-)
            if (String(selectedHistoryItem.id).startsWith('dummy-')) {
                 // Simulate API delay
                 await new Promise(resolve => setTimeout(resolve, 500));
                 toast.current.show({ severity: 'info', summary: 'Info', detail: 'Changes saved temporarily to dummy data!' });
                 setIsSaving(false);
                 return;
            }

            const token = localStorage.getItem('authToken');
            
            // Determine the user_id, role_id, type for the record owner
            let targetUserId;
            let targetRoleId;
            let targetType = 'projectManager'; // Default

            if (userData) {
                const parts = userData.split('-');
                if (parts.length >= 3) {
                    targetType = parts[0];
                    targetUserId = parts[1];
                    targetRoleId = parts[2];
                } else if (parts.length >= 2) {
                    targetUserId = parts[1];
                }
            }
            if (!targetUserId) {
                targetUserId = localStorage.getItem('user_id');
            }
            if (!targetRoleId) {
                targetRoleId = localStorage.getItem('role_id');
            }
            
            // FETCH EXISTING DATA FIRST to preserve discussion items, goals, etc.
            let existingData = {};
            let existingParentId = null;

            try {
                const fetchBody = {
                    userId: targetUserId,
                    role_id: targetRoleId,
                    type: targetType,
                    from_date: selectedHistoryItem.from_date,
                    to_date: selectedHistoryItem.to_date,
                };

                const fetchResponse = await fetch(`${Api.API_BASE_URL}performance-huddle/performance-points`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token ? `Bearer ${token}` : ''
                    },
                    body: JSON.stringify(fetchBody),
                });
                
                if (fetchResponse.ok) {
                    existingData = await fetchResponse.json();
                    
                     // Capture the Parent Record ID to ensure we update the CORRECT week
                    const parent = existingData.projectPerfomance || existingData.projectPerformance;
                    if (parent && parent.id) {
                        existingParentId = parent.id;
                    }
                }
            } catch(e) {
                console.warn("Error fetching existing record details", e);
            }

            const currentEditorId = localStorage.getItem('user_id');
            const timestamp = new Date().toISOString();

            // Prepare payload matching PmPerformancePoints structure
            const payload = {
                // Ensure we target the specific record ID if found, to prevent date overlap bugs
                id: existingParentId,
                projectPerfomanceID: existingParentId,

                user_id: targetUserId,
                role_id: targetRoleId, // explicit role_id at root
                type: targetType,     // explicit type at root

                from_date: selectedHistoryItem.from_date,
                to_date: selectedHistoryItem.to_date,
                
                // Preserve existing fields from the fetch
                discussionItem: existingData.discussionItem || [],
                goalForTheWeek: existingData.goalForTheWeek || [],
                toDiscuss: existingData.toDiscuss || [],
                
                // Add Away Status
                is_away: selectedHistoryItem.is_away ? 'Y' : 'N',
                away_reason: selectedHistoryItem.away_reason || '',

                performance: selectedHistoryItem.performance.map(item => ({
                    ...item,
                    // Ensure critical fields are present for update
                    pm_id: targetUserId,
                    role_id: item.role_id || targetRoleId,
                    type: targetType, 
                    active: 'Y',
                    from_date: selectedHistoryItem.from_date,
                    to_date: selectedHistoryItem.to_date,
                    // Force 'AWAY' value if is_away is true to ensure persistence
                    value: selectedHistoryItem.is_away ? 'AWAY' : (item.value || ''), 
                    is_away: selectedHistoryItem.is_away ? 'Y' : 'N',
                    away_reason: selectedHistoryItem.away_reason || '',
                    
                    // Metadata
                    created_by: item.created_by || currentEditorId,
                    created_at: item.created_at || timestamp,
                    updated_by: currentEditorId,
                    updated_at: timestamp
                }))
            };

            const response = await fetch(`${Api.API_BASE_URL}performance-huddle/performance-points-save`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify(payload),
            });
            
            // Handle response
            let result = null;
            try {
                result = await response.json();
            } catch(e) {
                throw new Error("Invalid server response");
            }
            
            if (!response.ok || (result.error && result.error !== '') || result.status === 'error') {
                throw new Error(result.error || result.message || 'Failed to save changes');
            }

            toast.current.show({ severity: 'success', summary: 'Success', detail: result.result || result.message || 'Changes saved successfully!' });
            
            // Refresh to ensure sync
            await fetchHistoryData();
            
        } catch (error) {
            console.error("Save failed", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to save changes: ${error.message || 'Unknown error'}` });
        } finally {
            setIsSaving(false);
        }
    };

    const headerStyle = {
        background: "#dddddd",
        border: "1px solid #E9ECEF",
        textAlign: "center",
        height: "24px",
        lineHeight: "1.2",
        fontWeight: 700,
    };

    const cellStyle = {
        borderLeft: "2px solid #E9ECEF",
        textAlign: "center",
        border: '1px solid white',
    };

    useEffect(() => {
        if (visible) {
            fetchHistoryData();
            setSelectedHistoryItem(null);
            // Default to current year when opening
            setSelectedYear(new Date().getFullYear());
        }
    }, [visible]);

    // Helper to generate all weeks (Mon-Sun) for a given year
    const getWeeksForYear = (year) => {
        const weeks = [];
        const date = new Date(year, 0, 1);
        // Find Monday of the first week
        const day = date.getDay();
        
        // Calculate the first Monday of the year (or just before)
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        const iteratorMonday = new Date(date);
        iteratorMonday.setDate(diff);
        iteratorMonday.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // --- DETERMINE "CURRENT ACTIVE WEEK" --- 
        // This week should NOT be in history yet because it is currently being worked on.
        // We use the same Holiday Logic as PmPerformancePoints.jsx to determine what "Current Week" is.

        const getActiveWeekStart = () => {
             const t = new Date(today);
             const dayOfWeek = t.getDay(); // 0 (Sun) to 6 (Sat)
             
             // Base Monday
             const m = new Date(t);
             m.setDate(t.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
             m.setHours(0, 0, 0, 0);

             // Logic: If Mon (or Tue-after-holiday), show Previous Week as Active.
             // If Previous Week is Active, then History stops at (Previous Week - 1).
             // If Current Week is Active, History stops at Previous Week.
             
             // 1. Check Holidays
             const isCanadianStatHoliday = (d) => {
                const date = d.getDate();
                const month = d.getMonth() + 1; // 1-12
                const year = d.getFullYear();
                
                // Fixed Date Holidays
                if (
                    (month === 1 && date === 1) || (month === 7 && date === 1) || (month === 9 && date === 30) ||
                    (month === 11 && date === 11) || (month === 12 && date === 25) || (month === 12 && date === 26)
                ) return true;
        
                // Floating Monday Holidays
                const isMonday = d.getDay() === 1;
                if (!isMonday) return false;
        
                // Family Day (3rd Mon Feb), Victoria Day (Mon before May 25), Civic (1st Mon Aug), Labour (1st Mon Sept), Thanksgiving (2nd Mon Oct)
                if (month === 2 && date >= 15 && date <= 21) return true;
                if (month === 5 && date >= 18 && date <= 24) return true;
                if (month === 8 && date <= 7) return true;
                if (month === 9 && date <= 7) return true;
                if (month === 10 && date >= 8 && date <= 14) return true;
                
                // Good Friday / Easter Monday (Simulated for 2024-2030)
                const easterMondays = ["2024-04-01", "2025-04-21", "2026-04-06", "2027-03-29", "2028-04-17", "2029-04-02", "2030-04-22"];
                const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(date).padStart(2,'0')}`;
                if (easterMondays.includes(dateStr)) return true;
        
                return false;
            };

            let isPreviousWeekActive = false;
            
            if (dayOfWeek === 1) {
                isPreviousWeekActive = true;
            } else if (dayOfWeek === 2) {
                const yesterday = new Date(t);
                yesterday.setDate(t.getDate() - 1);
                if (isCanadianStatHoliday(yesterday)) {
                    isPreviousWeekActive = true;
                }
            }

            if (isPreviousWeekActive) {
                // If previous week is active (being verified), history ends BEFORE that week.
                // So "Active Week" Date is (Current Monday - 7)
                // History should show weeks < (Current Monday - 7)
                const activeWeekDate = new Date(m);
                activeWeekDate.setDate(m.getDate() - 7);
                return activeWeekDate;
            } else {
                // Normal case: Current week is active. History shows weeks < Current Monday.
                return m;
            }
        };

        const activeWeekStart = getActiveWeekStart();

        // Iterate while the Monday is in the same year or the previous year (start of year edge case)
        // We stop when we reach the next year
        while (iteratorMonday.getFullYear() <= year) {
            
            // Let's stick to: If the Monday is in the year, display it.
            if (iteratorMonday.getFullYear() === year) {
                
                // Only include weeks strictly BEFORE the currently active week
                if (iteratorMonday < activeWeekStart) {
                    const currentSunday = new Date(iteratorMonday);
                    currentSunday.setDate(iteratorMonday.getDate() + 6);
                    
                    // Format YYYY-MM-DD local time to avoid timezone shifts
                    const format = (d) => {
                        const offset = d.getTimezoneOffset();
                        const local = new Date(d.getTime() - (offset*60*1000));
                        return local.toISOString().split('T')[0];
                    };

                    weeks.push({
                        dummyId: `generated-${format(iteratorMonday)}`,
                        from_date: format(iteratorMonday),
                        to_date: format(currentSunday),
                        isPlaceholder: true
                    });
                }
            }
            iteratorMonday.setDate(iteratorMonday.getDate() + 7);
        }
        return weeks.reverse(); // DESC order
    };

    // Update filtered data when selected year or history data changes
    useEffect(() => {
        // 1. Calculate Available Years (Data + Range)
        const dataYears = historyData.map(item => new Date(item.from_date).getFullYear());
        const currentYear = new Date().getFullYear();
        // Ensure we show at least current year and last 2 years, plus any data years
        const yearSet = new Set([...dataYears, currentYear, currentYear - 1, currentYear - 2]); 
        const sortedYears = Array.from(yearSet).sort((a, b) => b - a);
        setAvailableYears(sortedYears);

        // 2. Generate Weeks for Selected Year
        const allWeeks = getWeeksForYear(selectedYear);
        
        // 3. Merge Data
        const mergedHistory = allWeeks.map(week => {
            // Find existing
            const existing = historyData.find(h => h.from_date === week.from_date);
            
            if (existing) {
                return existing;
            }
            
            // If not found, create a placeholder using the specific logic requested:
            // Try to find the PREVIOUS week data to use as a template
            let templateItems = performanceItems || [];
            
            try {
                // Calculate previous week's date (subtract 7 days)
                const [y, m, d] = week.from_date.split('-').map(Number);
                const prevDate = new Date(y, m - 1, d);
                prevDate.setDate(prevDate.getDate() - 7);
                
                // Format safely to YYYY-MM-DD
                const pY = prevDate.getFullYear();
                const pM = String(prevDate.getMonth() + 1).padStart(2, '0');
                const pD = String(prevDate.getDate()).padStart(2, '0');
                const prevDateStr = `${pY}-${pM}-${pD}`;
                
                const prevWeekData = historyData.find(h => h.from_date === prevDateStr);
                
                if (prevWeekData && prevWeekData.performance && prevWeekData.performance.length > 0) {
                     // Check if it's a valid saved record (has ID or database flag) or just non-empty
                     // Since historyData comes from API, it should be saved data.
                     templateItems = prevWeekData.performance;
                }
            } catch (e) {
                console.warn("Error calculating previous week for history template", e);
            }

            return {
                ...week,
                performance: templateItems.map(t => {
                     // Careful to strip IDs and set values to 'no'
                     // Also handle legacy vs new field names if necessary (though historyData usually normalized)
                     return {
                        ...t,
                        category: t.category || '',
                        standard: t.standard || '',
                        deadline: t.deadline || '',
                        value: 'no', // Reset value for new week
                        score: 0,    // Reset item score if any
                        id: undefined, // ensure it's treated as new
                        performance_id: undefined
                    };
                }),
                score: 0
            };
        });

        setFilteredHistory(mergedHistory);

    }, [historyData, selectedYear]);
    
    const handleHistoryAwayChange = (e) => {
        const checked = e.checked;
        if (checked) {
             confirmDialog({
                message: (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ marginBottom: '15px', color: '#d32f2f', textAlign: 'center' }}>
                            <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', marginBottom: '10px' }}></i>
                            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Set as Away?</div>
                        </div>
                        <div style={{ textAlign: 'center', color: '#555', fontSize: '15px', lineHeight: '1.5' }}>
                            Marking as "Away" will automatically set<br/>
                            <strong>ALL Performance Points to 'NO'</strong>.
                            <br/><br/>
                            Do you want to continue?
                        </div>
                        <div style={{ height: '30px' }}></div>
                    </div>
                ),
                header: 'Confirm Away Status',
                icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                accept: () => {
                     // Set Is Away = true AND all items to AWAT
                     const updated = { ...selectedHistoryItem, is_away: true };
                     if (updated.performance) {
                         // Use 'AWAY' instead of 'no' to persist the status even if database doesn't store is_away column
                         updated.performance = updated.performance.map(p => ({...p, value: 'AWAY'}));
                         updated.score = 0;
                     }
                     updateLocalState(updated);
                },
                reject: () => {
                    // Do nothing
                },
                style: { width: '400px' }
            });
        } else {
            // Set Is Away = false
            const updated = { ...selectedHistoryItem, is_away: false };
            updateLocalState(updated);
        }
    };

    const handleHistoryAwayReasonChange = (val) => {
        const updated = { ...selectedHistoryItem, away_reason: val };
        updateLocalState(updated);
    };

    const updateLocalState = (updatedItem) => {
        setSelectedHistoryItem(updatedItem);
        const updatedHistoryData = historyData.map(item => 
            item.id === updatedItem.id ? updatedItem : item
        );
        setHistoryData(updatedHistoryData);
        setFilteredHistory(updatedHistoryData.filter(item => {
            const itemYear = new Date(item.from_date).getFullYear();
            return itemYear === selectedYear;
        }));
    };

    const getAverageScore = () => {
        // Filter out away weeks calculations
        const activeWeeks = filteredHistory.filter(item => !item.is_away);
        
        if (activeWeeks.length === 0) return 0;
        
        const totalScore = activeWeeks.reduce((acc, item) => {
            let score = item.score;
             if(score === undefined && item.performance) {
                const yes = item.performance.filter(p => (p.value || '').toLowerCase() === 'yes').length;
                score = (yes / 10) * 100;
            }
            return acc + (Number(score) || 0);
        }, 0);
        return Math.round(totalScore / activeWeeks.length);
    };

    const fetchHistoryData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            let user_id;
            let role_id;
            
            if (userData) {
                const parts = userData.split('-');
                if (parts.length >= 2) {
                    user_id = parts[1];
                    role_id = parts[2];
                }
            } 
            
            // Fallback to local storage if not derived from userData
            if (!user_id) {
                user_id = localStorage.getItem('user_id');
                role_id = localStorage.getItem('role_id');
            }

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };
            
            const response = await fetch(`${Api.API_BASE_URL}performance-huddle/user-performance-history`, {
                method: "POST",
                headers: headers,
                body: JSON.stringify({ users_id: user_id, role_id: role_id }),
            });
            
            // Try/Catch block to handle JSON parsing errors or non-OK responses gracefully
            let data = null;
            try {
                if (response.ok) {
                    data = await response.json();
                }
            } catch (e) {
                console.warn("Failed to parse API response", e);
            }
            
            if (data && (data.status === 200 || data.status === 'success')) {
               const rawList = data.data || data.result || [];
               const rootIsAway = data.is_away === 'Y';
               const rootAwayReason = data.away_reason || '';
               
               if (Array.isArray(rawList) && rawList.length > 0) {
                   console.log("Performance History Raw Data:", rawList); // Debug Log

                   // Group by date range
                   const grouped = {};
                   
                   // Helper to check truthiness of is_away (handles 'Y', 'y', true, 1)
                   const checkAway = (item) => {
                        const val = item.is_away;
                        const isFlag = val === 'Y' || val === 'y' || val === true || val === 1 || val === '1';
                        // Also check if value is marked as 'AWAY' (Workaround for backend persistence)
                        const isValueMarker = item.value === 'AWAY';
                        return isFlag || isValueMarker;
                   };

                   rawList.forEach(item => {
                       const dateKey = `${item.from_date}_${item.to_date}`;
                       const isAwayItem = checkAway(item);

                       if (!grouped[dateKey]) {
                           grouped[dateKey] = {
                               id: dateKey,
                               from_date: item.from_date,
                               to_date: item.to_date,
                               performance: [],
                               is_away: isAwayItem,
                               away_reason: item.away_reason || ''
                           };
                       }
                       // If any item in the group has is_away, ensure the group has it
                       if (isAwayItem) {
                            grouped[dateKey].is_away = true;
                            grouped[dateKey].away_reason = item.away_reason || grouped[dateKey].away_reason;
                       }
                       grouped[dateKey].performance.push(item);
                   });

                   const historyList = Object.values(grouped).map(group => {
                       // Simplified Logic: 
                       // 1. Use a Map to deduplicate standards.
                       // 2. Preserve the order from the API (First occurrence creates the entry/slot).
                       // 3. If a standard appears again (e.g. edited), update the value if it's newer.
                       
                       const uniqueMap = new Map();
                       
                       group.performance.forEach(item => {
                           const key = (item.standard || '').trim();
                           
                           if (!uniqueMap.has(key)) {
                               // First time seeing this standard -> Add it (preserves API order)
                               uniqueMap.set(key, item);
                           } else {
                               // Duplicate found -> Keep the newest one
                               const existing = uniqueMap.get(key);
                               const existingTime = new Date(existing.created_at || existing.createdAt || 0).getTime();
                               const newItemTime = new Date(item.created_at || item.createdAt || 0).getTime();
                               
                               if (newItemTime > existingTime) {
                                   uniqueMap.set(key, item);
                               }
                           }
                       });

                       const finalItems = Array.from(uniqueMap.values());
                       group.performance = finalItems;
                       
                       // Recalculate Score
                       const yesCount = finalItems.filter(p => (p.value || '').toLowerCase() === 'yes').length;
                       
                       // Check Priority Fail Conditions (Items 1 & 2)
                       // We used fuzzy matching before, let's keep it safe by checking text content
                       // or rely on the order if user confirms item 1/2 are always first.
                       // Let's use a robust text check to be safe.
                       
                       const normalize = (s) => (s || '').toLowerCase().replace(/['’‘]/g, "'").replace(/\s+/g, ' ').trim();
                       const t1Key = normalize(performanceItems[0]?.standard || '');
                       const t2Key = normalize(performanceItems[1]?.standard || '');
                       
                       let failPriority = false;
                       finalItems.forEach(item => {
                           const k = normalize(item.standard);
                           // Check if it matches first two standards
                           if ((k === t1Key || k === t2Key) && (item.value || '').toLowerCase() !== 'yes') {
                               failPriority = true;
                           }
                       });
                       
                       const score = failPriority ? 0 : (yesCount / 10) * 100;
                       
                       return { ...group, score };
                   });

                   // Sort by date descending
                   historyList.sort((a, b) => new Date(b.from_date) - new Date(a.from_date));
                   
                   // Apply root-level is_away status to the specific week if provided
                   if (rootIsAway) {
                        // Extract YYYY-MM-DD from the root ISO string
                        const targetFrom = data.from_date ? data.from_date.split('T')[0] : null;
                        
                        // Find the matching week by start date
                        const targetWeek = historyList.find(h => h.from_date === targetFrom);
                        
                        if (targetWeek) {
                            targetWeek.is_away = true;
                            targetWeek.away_reason = rootAwayReason;
                        } else if (!historyList.some(h => h.is_away) && historyList.length > 0) {
                             // Fallback: If no date match found (or no date provided) and no other week is away,
                             // assume it applies to the most recent week.
                             historyList[0].is_away = true;
                             historyList[0].away_reason = rootAwayReason;
                        }
                   }

                   setHistoryData(historyList);
               } else {
                   setHistoryData([]);
               }
            } else {
               setHistoryData([]);
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            setHistoryData([]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog 
            header={<div className="flex align-items-center"><FaHistory className="mr-2"/> Performance Points History</div>} 
            visible={visible} 
            style={{ width: '90vw', maxWidth: '1200px', height: '85vh' }}
            onHide={onHide}
            className="history-dialog"
        >
            <Toast ref={toast} />

            <div style={{ display: 'flex', height: '100%', gap: '20px', padding: '0 20px' }}>
                {/* Left Sidebar: Week List */}
                <div style={{ flex: '0 0 250px', borderRight: '1px solid #e9ecef', overflowY: 'auto', paddingRight: '10px' }}>
                    
                    {/* Year Selector */}
                    <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <label style={{display: 'block', fontSize: '12px', marginBottom: '5px', color: '#666'}}>Select Year:</label>
                        <select 
                            value={selectedYear} 
                            onChange={(e) => {
                                setSelectedYear(Number(e.target.value));
                                setSelectedHistoryItem(null); // Return to overview when year changes
                            }}
                            style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        >
                            {availableYears.length > 0 ? (
                                availableYears.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))
                            ) : (
                                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                            )}
                        </select>
                    </div>

                    {isLoading ? (
                        <div className="text-center p-3"><i className="pi pi-spin pi-spinner" style={{fontSize: '2rem'}}></i></div>
                    ) : filteredHistory.length === 0 ? (
                        <div className="text-center p-3 text-gray-500">No records for {selectedYear}</div>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {filteredHistory.map((item, index) => {
                                let dateLabel = `Week ${index + 1}`;
                                if(item.from_date && item.to_date) {
                                    try {
                                        // Replaced with simple string format as requested
                                        dateLabel = `${item.from_date} to ${item.to_date}`;
                                    } catch(e) {}
                                }
                                
                                const isSelected = selectedHistoryItem === item;
                                
                                let itemScore = item.score;
                                if(itemScore === undefined && item.performance) {
                                    const yes = item.performance.filter(p => p.value === 'yes').length;
                                    itemScore = (yes / 10) * 100;
                                }
                                
                                return (
                                    <li 
                                        key={index}
                                        onClick={() => setSelectedHistoryItem(item)}
                                        style={{
                                            padding: '12px 15px',
                                            borderBottom: '1px solid #f8f9fa',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                            color: isSelected ? '#1976d2' : '#495057',
                                            fontWeight: isSelected ? '600' : 'normal',
                                            borderRadius: '6px',
                                            marginBottom: '4px',
                                            transition: 'background-color 0.2s',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                        className="history-list-item"
                                    >
                                        <div style={{fontSize: '14px'}}>{dateLabel}</div>
                                        <div style={{fontSize: '12px', color: '#888', marginTop: '4px', display: 'flex', justifyContent: 'space-between'}}>
                                            <span>Score: {itemScore}%</span>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </div>

                {/* Right Content: Details */}
                <div style={{ flex: 1, overflowY: 'auto', paddingLeft: '10px' }}>
                    {selectedHistoryItem ? (
                        <div>
                            <h3 style={{marginTop: 0, borderBottom: '2px solid #eee', paddingBottom: '10px', color: '#333', fontSize: '18px'}}>
                                Performance Standard 
                                {selectedHistoryItem.from_date && (
                                    <span style={{fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px'}}>
                                        ({selectedHistoryItem.from_date} to {selectedHistoryItem.to_date})
                                    </span>
                                )}
                            </h3>
                            
                            <div style={{ padding: '20px 20px 10px 20px', borderBottom: '1px solid #eee', backgroundColor: '#fff8e1', marginBottom: '20px', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: selectedHistoryItem.is_away ? '15px' : '0' }}>
                                    <Checkbox 
                                        inputId="hist_is_away" 
                                        checked={selectedHistoryItem.is_away} 
                                        onChange={handleHistoryAwayChange} 
                                        disabled={!isAdmin}
                                    />
                                    <label htmlFor="hist_is_away" style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600', color: '#856404', cursor: isAdmin ? 'pointer' : 'default', display: 'flex', alignItems: 'center' }}>
                                        Was the user Away? (Holiday, Sick, etc.)
                                    </label>
                                </div>
                                
                                {selectedHistoryItem.is_away && (
                                    <div style={{ marginLeft: '32px', marginTop: '10px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: '#666' }}>
                                            Reason / Comments:
                                        </label>
                                        <InputTextarea 
                                            value={selectedHistoryItem.away_reason || ''} 
                                            onChange={(e) => handleHistoryAwayReasonChange(e.target.value)} 
                                            rows={3} 
                                            style={{ width: '100%', maxWidth: '100%', borderColor: '#ccc' }} 
                                            autoResize
                                            placeholder="Please provide details about the absence..."
                                            disabled={!isAdmin}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="table-container header-fixed" style={{ overflowX: "auto" }}>
                                <table style={{ borderCollapse: "collapse", width: '100%', marginBottom: '20px' }}>
                                    <thead>
                                        <tr>
                                            <th style={headerStyle}>#</th>
                                            <th style={{...headerStyle, width: '30%'}}>Category</th>
                                            <th style={headerStyle}>Performance Standard</th>
                                            <th style={headerStyle}>Deadline</th>
                                            <th style={{...headerStyle, width: '60px'}}>Yes</th>
                                            <th style={{...headerStyle, width: '60px'}}>No</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedHistoryItem.performance && Array.isArray(selectedHistoryItem.performance) && selectedHistoryItem.performance.map((pItem, idx) => {
                                            // Priority Row Styling (Top 2 items)
                                            const rowBgColor = (idx === 0 || idx === 1) ? '#fff8e1' : '#fff';
                                            const rowBorderBottom = (idx === 1) ? '2px solid #ffc107' : '1px solid #eee';

                                            return (
                                            <tr key={idx} style={{ borderBottom: rowBorderBottom, backgroundColor: rowBgColor }}>
                                                <td style={{ ...cellStyle, textAlign: 'center', backgroundColor: rowBgColor }}>{idx + 1}</td>
                                                <td style={{ ...cellStyle, textAlign: 'left', padding: '0', backgroundColor: rowBgColor }}>
                                                    <InputTextarea 
                                                        style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', fontSize: '14px', color: '#495057', paddingTop: '8px', padding: '4px', backgroundColor: 'transparent', fontFamily: 'inherit' }}
                                                        value={pItem.category || ''}
                                                        onChange={(e) => handleItemChange(idx, 'category', e.target.value)}
                                                        autoResize
                                                        rows={1}
                                                        disabled={!isAdmin}
                                                    />
                                                </td>
                                                <td style={{ ...cellStyle, textAlign: 'left', padding: '0', backgroundColor: rowBgColor }}>
                                                    <InputTextarea 
                                                        style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', fontSize: '14px', color: '#495057', paddingTop: '8px', padding: '4px', backgroundColor: 'transparent', fontFamily: 'inherit' }}
                                                        value={pItem.standard || ''}
                                                        onChange={(e) => handleItemChange(idx, 'standard', e.target.value)}
                                                        autoResize
                                                        rows={1}
                                                        disabled={!isAdmin}
                                                    />
                                                </td>
                                                <td style={{ ...cellStyle, textAlign: 'center', padding: '0', backgroundColor: rowBgColor }}>
                                                    <InputTextarea 
                                                        style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', fontSize: '14px', color: '#495057', paddingTop: '8px', padding: '4px', backgroundColor: 'transparent', fontFamily: 'inherit', textAlign: 'center' }}
                                                        value={pItem.deadline || ''}
                                                        onChange={(e) => handleItemChange(idx, 'deadline', e.target.value)}
                                                        autoResize
                                                        rows={1}
                                                        disabled={!isAdmin}
                                                    />
                                                </td>
                                                
                                                {/* Edit Columns - Yes */}
                                                <td style={{ 
                                                    ...cellStyle, 
                                                    textAlign: 'center', 
                                                    padding: '0', 
                                                    verticalAlign: 'middle',
                                                    backgroundColor: pItem.value === 'yes' ? '#e8f5e9' : rowBgColor 
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '5px' }}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={pItem.value === 'yes'}
                                                            onChange={() => handleItemChange(idx, 'value', 'yes')}
                                                            disabled={!isAdmin} 
                                                            style={{ 
                                                                width: '24px', 
                                                                height: '24px', 
                                                                cursor: isAdmin ? 'pointer' : 'not-allowed', 
                                                                accentColor: '#2e7d32' 
                                                            }}
                                                        />
                                                    </div>
                                                </td>

                                                {/* Edit Columns - No */}
                                                <td style={{ 
                                                    ...cellStyle, 
                                                    textAlign: 'center', 
                                                    padding: '0', 
                                                    verticalAlign: 'middle',
                                                    backgroundColor: (pItem.value === 'no' || pItem.value === 'AWAY') ? '#ffebee' : rowBgColor 
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '5px' }}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={pItem.value === 'no' || pItem.value === 'AWAY'}
                                                            onChange={() => handleItemChange(idx, 'value', 'no')}
                                                            disabled={!isAdmin} 
                                                            style={{ 
                                                                width: '24px', 
                                                                height: '24px', 
                                                                cursor: isAdmin ? 'pointer' : 'not-allowed', 
                                                                accentColor: '#d32f2f' 
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )})}
                                    </tbody>
                                </table>

                                {/* Weekly Score Summary & Hard Fail Rules */}
                                {(() => {
                                    const performance = selectedHistoryItem.performance || [];
                                    const yesCount = performance.filter(p => p.value === 'yes').length;
                                    const noCount = performance.filter(p => p.value === 'no').length;
                                    const weeklyScore = selectedHistoryItem.score !== undefined ? Math.round(selectedHistoryItem.score) : 0;
                                    const isPriorityFail = performance.length > 1 && (performance[0].value === 'no' || performance[1].value === 'no');
                                    
                                    return (
                                        <div style={{ marginTop: '20px', marginBottom: '20px', padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                            <h4 style={{ margin: '0 0 15px 0', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>Weekly Score Summary</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '20px' }}>
                                                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
                                                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Items</div>
                                                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>10</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                                                    <div style={{ fontSize: '13px', color: '#1565c0', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>YES Count</div>
                                                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>{yesCount}</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#ffebee', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
                                                    <div style={{ fontSize: '13px', color: '#c62828', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NO Count</div>
                                                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#d32f2f' }}>{noCount}</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                                                    <div style={{ fontSize: '13px', color: '#2e7d32', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Weekly Score</div>
                                                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2e7d32' }}>{weeklyScore}%</div>
                                                </div>
                                            </div>

                                            <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                                <h4 style={{ margin: '0 0 10px 0', color: '#d32f2f', fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                                                    Hard Fail Rules 
                                                    {isPriorityFail && 
                                                        <span style={{ marginLeft: '10px', backgroundColor: '#d32f2f', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}}>FAIL TRIGGERED</span>
                                                    }
                                                </h4>
                                                <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontStyle: 'italic' }}>Regardless of percentage, the week is an automatic FAIL if the first two rows are not met:</p>
                                                <ul style={{ margin: '0 0 0 20px', padding: 0, fontSize: '14px', lineHeight: '1.6' }}>
                                                    <li style={performance[0]?.value === 'no' ? {color: '#d32f2f', fontWeight: 'bold'} : {}}>
                                                        Item #1 (Priority Rule) = <strong>NO</strong> 
                                                        {performance[0]?.value === 'no' && " ❌"}
                                                    </li>
                                                    <li style={performance[1]?.value === 'no' ? {color: '#d32f2f', fontWeight: 'bold'} : {}}>
                                                        Item #2 (Priority Rule) = <strong>NO</strong>
                                                        {performance[1]?.value === 'no' && " ❌"}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '15px', marginBottom: '25px'}}>
                                    <button 
                                        className="p-button p-component" 
                                        onClick={handleSave}
                                        disabled={isSaving || !isAdmin}
                                        style={{ backgroundColor: isAdmin ? '#4CAF50' : '#cccccc', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: isAdmin ? 'pointer' : 'not-allowed' }}
                                    >
                                        {isSaving ? <i className="pi pi-spin pi-spinner"></i> : <i className="pi pi-save mr-2"></i>}
                                        <span style={{marginLeft: '8px'}}>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#333' }}>
                            {/* Year Overview Dashboard */}
                            <div style={{ padding: '30px', backgroundColor: '#f9f9f9', borderRadius: '12px', border: '1px solid #eee', width: '90%', maxWidth: '750px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                                <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', color: '#2c3e50' }}>{selectedYear} Performance Overview</h2>
                                <p style={{ margin: '0 0 25px 0', color: '#888', fontSize: '14px' }}>Summary of performance points for the selected year</p>
                                
                                <div style={{ marginBottom: '25px' }}>
                                    <div style={{ fontSize: '13px', textTransform: 'uppercase', color: '#666', letterSpacing: '1px', marginBottom: '5px' }}>Average Score</div>
                                    <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#2e7d32' }}>{getAverageScore()}%</div>
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                                    <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #eee' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Weeks Records</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                            {filteredHistory.filter(h => !h.is_away).length}
                                        </div>
                                    </div>
                                    <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #eee' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Weeks Away</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f57c00' }}>
                                            {filteredHistory.filter(h => h.is_away).length}
                                        </div>
                                    </div>
                                    <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #eee' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Perfect Weeks (100%)</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1976d2' }}>
                                            {filteredHistory.filter(h => {
                                                const score = h.score !== undefined ? h.score : ((h.performance?.filter(p => p.value === 'yes').length || 0) / 10) * 100;
                                                return Number(score) === 100;
                                            }).length}
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ marginTop: '30px', fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                                    Select a specific week from the sidebar to view full details.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default PerformanceHistoryModal;
